import React, { useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "sonner";
import { addMember } from "../../api/settings";
import { AddCentre } from "../../api/settings";
import { generateTemplateWorkbook } from "./templateData";

// ─── helpers ─────────────────────────────────────────────────────────────────

function toBool(val) {
  if (val === true || val === false) return val;
  const s = String(val ?? "").trim().toUpperCase();
  return s === "TRUE" || s === "YES" || s === "1";
}

function parseRow(headers, rowArr) {
  const obj = {};
  headers.forEach((h, i) => {
    obj[h] = rowArr[i] !== undefined ? String(rowArr[i] ?? "").trim() : "";
  });
  return obj;
}

function buildCenterPayload(row) {
  // tempCenterId is import-only — never sent to the API
  const isSame = toBool(row.billing_isSameAsAddress);
  return {
    organisationName: row.centreName,
    centerRole: row.centerRole,
    type: row.type,
    phone_number: row.phoneNumber,
    phoneNumber: row.phoneNumber,
    organisationPhone: row.phoneNumber,
    countryCode: row.countryCode || "+91",
    email: row.email,
    address: {
      floor: row.address_floor,
      area: row.address_area,
      city: row.address_city,
      state: row.address_state,
      pincode: row.address_pincode,
      country: row.address_country || "India",
      fulladdress: row.address_fulladdress,
      latitude: row.address_latitude ? parseFloat(row.address_latitude) : undefined,
      longitude: row.address_longitude ? parseFloat(row.address_longitude) : undefined,
    },
    billingDetails: {
      name: row.billing_name,
      code: row.billing_code,
      taxId: row.billing_taxId,
      isSameAsAddress: isSame,
      address: {
        line: isSame ? row.address_fulladdress : row.billing_line,
        city: isSame ? row.address_city : row.billing_city,
        state: isSame ? row.address_state : row.billing_state,
        pincode: isSame ? row.address_pincode : row.billing_pincode,
        country: "India",
      },
    },
    isSameAsAddress: isSame,
    abha_facility_id: row.abha_facility_id || undefined,
    canInterpretReport: toBool(row.canInterpretReport),
    hub_centers: row.hub_center_ids
      ? row.hub_center_ids.split(",").map((s) => s.trim()).filter(Boolean)
      : [],
    centerLogo: "",
    centerImages: "",
  };
}

function buildPermissions(row) {
  const isDoctor = row.role === "b2b-doctor";

  const permissions = {
    peopleManagement: {
      canManageUsers: toBool(row.perm_canManageUsers),
    },
    ecgCollectionAndReports: {
      canViewRequests: toBool(row.perm_canViewRequests),
      canManageRequests: toBool(row.perm_canManageRequests),
      canViewReports: toBool(row.perm_canViewReports),
      canShareReports: toBool(row.perm_canShareReports),
    },
    ecgInterpretation: {
      canSendForInterpretation: toBool(row.perm_canSendForInterpretation),
      canInterpreteReport: toBool(row.perm_canInterpreteReport),
    },
    applicationUses: {
      canAccessApp: toBool(row.perm_canAccessApp),
    },
  };

  if (isDoctor) {
    permissions.cases = {
      canReferCases: toBool(row.perm_canReferCases),
      canViewCases: toBool(row.perm_canViewCases),
    };
  }

  // dependency rule: canAccessApp enables canManageRequests + canViewRequests
  if (permissions.applicationUses.canAccessApp) {
    if (permissions.ecgCollectionAndReports.canManageRequests) {
      permissions.ecgCollectionAndReports.canViewRequests = true;
    }
  }

  return permissions;
}

function buildMemberPayload(row) {
  return {
    firstName: row.firstName,
    middleName: row.middleName || "",
    lastName: row.lastName || "",
    email: row.email || "",
    phoneNumber: row.phoneNumber || "",
    countryCode: row.countryCode || "+91",
    virtualId: row.virtualId || "",
    defaultPassword: row.defaultPassword || "",
    age: row.age ? Number(row.age) : undefined,
    gender: row.gender || "",
    dateOfBirth: row.dateOfBirth || "",
    profession: row.profession || "",
    qualification: row.qualification
      ? row.qualification.split(",").map((s) => s.trim()).filter(Boolean)
      : [],
    medicalLicenseNumber: row.medicalLicenseNumber || "",
    role: row.role,
    designation: row.designation || "",
    centerIds: row.centerIds
      ? row.centerIds.split(",").map((s) => s.trim()).filter(Boolean)
      : [],
    permissions: buildPermissions(row),
    skipOnboarding: true,
  };
}

const VALID_CENTER_TYPES = [
  "Private Practice Clinic",
  "Private Diagnostic centre",
  "Government Facility",
  "Private AYUSH clinic",
  "Private Hospital",
];

const VALID_PROFESSIONS = [
  "Doctor", "Nurse", "Lab Technician", "Pharmacist",
  "Non-Medical Professional", "Medical Student",
  "Diagnostics Professional", "Other Medical Professional",
];

const VALID_ROLES = ["b2b-admin", "b2b-member", "b2b-doctor", "b2b-viewer"];

const EXPECTED_CENTER_HEADERS = [
  "centreName","centerRole","type","phoneNumber","countryCode","email",
  "address_floor","address_area","address_city","address_state","address_pincode",
  "address_country","address_fulladdress","address_latitude","address_longitude",
  "billing_name","billing_code","billing_taxId","billing_isSameAsAddress",
  "billing_line","billing_city","billing_state","billing_pincode",
  "abha_facility_id","canInterpretReport","hub_center_ids","tempCenterId",
];

const EXPECTED_MEMBER_HEADERS = [
  "firstName","middleName","lastName","email","phoneNumber","countryCode",
  "virtualId","defaultPassword","age","gender","dateOfBirth","profession",
  "qualification","medicalLicenseNumber","role","designation","centerIds",
  "perm_canManageUsers","perm_canViewRequests","perm_canManageRequests",
  "perm_canViewReports","perm_canShareReports","perm_canSendForInterpretation",
  "perm_canInterpreteReport","perm_canAccessApp","perm_canReferCases","perm_canViewCases",
];

function checkHeaders(actual, expected, sheetName) {
  const missing = expected.filter((h) => !actual.includes(h));
  if (missing.length) {
    return `${sheetName} sheet is missing columns: ${missing.join(", ")}. Please use the official template.`;
  }
  return null;
}

// allTempIds — Set of tempCenterId values from the Centers sheet (for cross-validation)
function validateCenter(row, allTempIds) {
  const errors = [];

  if (!row.centreName?.trim()) errors.push("centreName required");

  if (!["Hub Centre", "Spoke Centre"].includes(row.centerRole))
    errors.push("centerRole must be exactly 'Hub Centre' or 'Spoke Centre'");

  if (!row.type?.trim()) errors.push("type required");
  else if (!VALID_CENTER_TYPES.includes(row.type))
    errors.push(`type must be one of: ${VALID_CENTER_TYPES.join(" | ")}`);

  if (!row.address_fulladdress?.trim()) errors.push("address_fulladdress required");

  if (row.phoneNumber?.trim() && !/^\d{10}$/.test(row.phoneNumber.trim()))
    errors.push("phoneNumber must be exactly 10 digits");

  if (!row.billing_name?.trim()) errors.push("billing_name required");
  if (!row.billing_code?.trim()) errors.push("billing_code required");
  if (!row.billing_taxId?.trim()) errors.push("billing_taxId required");
  if (!row.billing_line?.trim()) errors.push("billing_line required");
  if (!row.billing_city?.trim()) errors.push("billing_city required");
  if (!row.billing_state?.trim()) errors.push("billing_state required");
  if (!row.billing_pincode?.trim()) errors.push("billing_pincode required");
  else if (!/^\d{6}$/.test(row.billing_pincode.trim()))
    errors.push("billing_pincode must be exactly 6 digits");

  if (!row.tempCenterId?.trim())
    errors.push("tempCenterId required (e.g. C1, C2) — needed for member mapping");

  if (row.centerRole === "Spoke Centre") {
    if (!row.hub_center_ids?.trim()) {
      errors.push("hub_center_ids required for Spoke Centre");
    } else {
      const hubIds = row.hub_center_ids.split(",").map((s) => s.trim()).filter(Boolean);
      const invalid = hubIds.filter((id) => !allTempIds.has(id));
      if (invalid.length)
        errors.push(`hub_center_ids references unknown tempCenterId: ${invalid.join(", ")}`);
    }
  }

  return errors;
}

function validateMember(row, allTempIds) {
  const errors = [];

  if (!row.firstName?.trim()) errors.push("firstName required");

  if (!row.phoneNumber?.trim() && !row.virtualId?.trim())
    errors.push("phoneNumber or virtualId required");

  if (row.phoneNumber?.trim() && !/^\d{10}$/.test(row.phoneNumber.trim()))
    errors.push("phoneNumber must be exactly 10 digits");

  if (!row.gender?.trim()) errors.push("gender required");
  else if (!["Male", "Female", "Other"].includes(row.gender.trim()))
    errors.push("gender must be exactly: Male | Female | Other");

  if (!row.dateOfBirth?.trim()) errors.push("dateOfBirth required");
  else if (!/^\d{2}-\d{2}-\d{4}$/.test(row.dateOfBirth.trim()))
    errors.push("dateOfBirth must be DD-MM-YYYY (e.g. 15-08-1990)");

  if (!row.profession?.trim()) errors.push("profession required");
  else if (!VALID_PROFESSIONS.includes(row.profession.trim()))
    errors.push(`profession must be one of: ${VALID_PROFESSIONS.join(" | ")}`);

  if (!VALID_ROLES.includes(row.role?.trim()))
    errors.push(`role must be one of: ${VALID_ROLES.join(", ")}`);

  if (row.age?.trim() && (isNaN(Number(row.age)) || Number(row.age) < 1 || Number(row.age) > 120))
    errors.push("age must be a number between 1 and 120");

  if (row.defaultPassword?.trim()) {
    const p = row.defaultPassword.trim();
    if (p.length < 8) errors.push("defaultPassword must be at least 8 characters");
    else if (!/[A-Z]/.test(p)) errors.push("defaultPassword must contain at least 1 uppercase letter");
    else if (!/[^a-zA-Z0-9]/.test(p)) errors.push("defaultPassword must contain at least 1 special character");
  }

  if (!row.centerIds?.trim()) {
    errors.push("centerIds required");
  } else {
    const ids = row.centerIds.split(",").map((s) => s.trim()).filter(Boolean);
    if (allTempIds.size > 0) {
      const invalid = ids.filter((id) => !allTempIds.has(id));
      if (invalid.length)
        errors.push(`centerIds references unknown tempCenterId: ${invalid.join(", ")}`);
    }
  }

  return errors;
}

// ─── Status chip ─────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  pending: { bg: "#F3F4F6", color: "var(--app-text-muted)", label: "Pending" },
  importing: { bg: "#EFF6FF", color: "#1D4ED8", label: "Importing..." },
  success: { bg: "#DAF4E1", color: "#348530", label: "Imported" },
  error: { bg: "#FFEBE8", color: "#D32F2F", label: "Failed" },
  skipped: { bg: "#FFF3CD", color: "#E4841A", label: "Skipped" },
};

function StatusChip({ status, error }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const chip = (
    <Chip
      label={s.label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "12px",
        borderRadius: "6px",
        backgroundColor: s.bg,
        color: s.color,
        fontFamily: "Figtree",
      }}
    />
  );
  if ((status === "error" || status === "skipped") && error) {
    return <Tooltip title={error} placement="left"><span>{chip}</span></Tooltip>;
  }
  return chip;
}

const headSx = {
  fontSize: "12px",
  fontWeight: 700,
  color: "var(--app-text-muted)",
  borderBottom: "none",
  fontFamily: "Figtree",
  whiteSpace: "nowrap",
  backgroundColor: "var(--app-surface-2)",
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function ImportOnboarding() {
  const fileRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [centers, setCenters] = useState([]);
  const [members, setMembers] = useState([]);
  const [centerStatuses, setCenterStatuses] = useState([]);
  const [memberStatuses, setMemberStatuses] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const [xlsxError, setXlsxError] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);

  const parseFile = useCallback(async (file) => {
    const XLSX = window.XLSX;
    if (!XLSX) {
      setXlsxError(true);
      toast.error("xlsx library failed to load. Please check your internet connection and refresh.");
      return;
    }

    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: "array" });

    const sheetNames = wb.SheetNames;
    const errors = [];

    if (!sheetNames.includes("Centers") && !sheetNames.includes("Members")) {
      setFileErrors(["File does not contain a 'Centers' or 'Members' sheet. Please use the official template."]);
      return;
    }

    // ── Centers ───────────────────────────────────────────────────────────────
    const centerSheet = wb.Sheets["Centers"];
    const allTempIds = new Set(); // built first — used for cross-validation in both sheets

    if (centerSheet) {
      const rows = XLSX.utils.sheet_to_json(centerSheet, { header: 1, defval: "" });
      const headers = rows[0]?.map((h) => String(h).trim()) ?? [];

      const headerErr = checkHeaders(headers, EXPECTED_CENTER_HEADERS, "Centers");
      if (headerErr) {
        errors.push(headerErr);
      } else {
        // row 0 = headers, row 1 = notes, rows 2+ = data
        const dataRows = rows.slice(2).filter((r) => r.some((c) => String(c).trim()));
        // First pass: collect all tempCenterIds for cross-validation
        dataRows.forEach((r) => {
          const row = parseRow(headers, r);
          if (row.tempCenterId?.trim()) allTempIds.add(row.tempCenterId.trim());
        });
        // Second pass: validate each row (hub_center_ids cross-check uses allTempIds)
        const parsed = dataRows.map((r, i) => {
          const row = parseRow(headers, r);
          const rowErrors = validateCenter(row, allTempIds);
          return { ...row, _index: i, _errors: rowErrors };
        });
        // Check tempCenterId uniqueness
        const seen = new Map();
        parsed.forEach((row, i) => {
          const tid = row.tempCenterId?.trim();
          if (tid) {
            if (seen.has(tid)) {
              row._errors.push(`tempCenterId '${tid}' is duplicated (also used in row ${seen.get(tid) + 3})`);
            } else {
              seen.set(tid, i);
            }
          }
        });
        setCenters(parsed);
        setCenterStatuses(parsed.map((r) => ({
          status: r._errors.length ? "skipped" : "pending",
          error: r._errors.join("; "),
        })));
      }
    }

    // ── Members ───────────────────────────────────────────────────────────────
    const memberSheet = wb.Sheets["Members"];
    if (memberSheet) {
      const rows = XLSX.utils.sheet_to_json(memberSheet, { header: 1, defval: "" });
      const headers = rows[0]?.map((h) => String(h).trim()) ?? [];

      const headerErr = checkHeaders(headers, EXPECTED_MEMBER_HEADERS, "Members");
      if (headerErr) {
        errors.push(headerErr);
      } else {
        const dataRows = rows.slice(2).filter((r) => r.some((c) => String(c).trim()));
        const parsed = dataRows.map((r, i) => {
          const row = parseRow(headers, r);
          const rowErrors = validateMember(row, allTempIds);
          return { ...row, _index: i, _errors: rowErrors };
        });
        setMembers(parsed);
        setMemberStatuses(parsed.map((r) => ({
          status: r._errors.length ? "skipped" : "pending",
          error: r._errors.join("; "),
        })));
      }
    }

    setFileErrors(errors);
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setCenters([]);
    setMembers([]);
    setCenterStatuses([]);
    setMemberStatuses([]);
    setFileErrors([]);
    setProgress(0);
    await parseFile(file);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setCenters([]);
    setMembers([]);
    setCenterStatuses([]);
    setMemberStatuses([]);
    setFileErrors([]);
    setProgress(0);
    await parseFile(file);
  };

  const handleImport = async () => {
    setImporting(true);
    const total = centers.filter((_, i) => centerStatuses[i]?.status === "pending").length
      + members.filter((_, i) => memberStatuses[i]?.status === "pending").length;
    let done = 0;

    // ── 1. Import Centers ──
    // Maps tempCenterId (e.g. "C1") → real api_key after each center is created.
    // Used to resolve hub_center_ids for spokes and centerIds for members.
    const centerRefMap = {};

    const resolve = (val) => centerRefMap[val?.trim()] || val?.trim();

    const newCenterStatuses = [...centerStatuses];
    for (let i = 0; i < centers.length; i++) {
      if (centerStatuses[i]?.status !== "pending") continue;

      newCenterStatuses[i] = { status: "importing", error: "" };
      setCenterStatuses([...newCenterStatuses]);

      try {
        const row = centers[i];
        const resolvedRow = {
          ...row,
          hub_center_ids: row.hub_center_ids
            ? row.hub_center_ids.split(",").map(resolve).join(",")
            : "",
        };

        const payload = buildCenterPayload(resolvedRow);
        const formData = new FormData();
        formData.append("centerDetails", JSON.stringify(payload));
        const res = await AddCentre(formData);
        if (res?.success || res?.result) {
          const apiKey = res?.user?.api_key || res?.user?.centerId;
          if (apiKey && row.tempCenterId) {
            centerRefMap[row.tempCenterId.trim()] = apiKey;
          }
          newCenterStatuses[i] = { status: "success", error: "" };
        } else {
          newCenterStatuses[i] = { status: "error", error: res?.message || "Failed" };
        }
      } catch (err) {
        newCenterStatuses[i] = { status: "error", error: err?.response?.data?.message || err?.message || "Unexpected error" };
      }

      done++;
      setProgress(Math.round((done / total) * 100));
      setCenterStatuses([...newCenterStatuses]);
    }

    // ── 2. Import Members ──
    const newMemberStatuses = [...memberStatuses];
    for (let i = 0; i < members.length; i++) {
      if (memberStatuses[i]?.status !== "pending") continue;

      newMemberStatuses[i] = { status: "importing", error: "" };
      setMemberStatuses([...newMemberStatuses]);

      try {
        const row = members[i];
        const resolvedCenterIds = row.centerIds
          ? row.centerIds.split(",").map(resolve).join(",")
          : "";
        const payload = buildMemberPayload({ ...row, centerIds: resolvedCenterIds });
        const res = await addMember(payload);
        if (res?.success || res?.result) {
          newMemberStatuses[i] = { status: "success", error: "" };
        } else {
          newMemberStatuses[i] = { status: "error", error: res?.message || "Failed" };
        }
      } catch (err) {
        newMemberStatuses[i] = { status: "error", error: err?.response?.data?.message || err?.message || "Unexpected error" };
      }

      done++;
      setProgress(Math.round((done / total) * 100));
      setMemberStatuses([...newMemberStatuses]);
    }

    setImporting(false);

    const centerFailed = newCenterStatuses.filter((s) => s.status === "error").length;
    const memberFailed = newMemberStatuses.filter((s) => s.status === "error").length;
    const centerOk = newCenterStatuses.filter((s) => s.status === "success").length;
    const memberOk = newMemberStatuses.filter((s) => s.status === "success").length;

    if (centerFailed + memberFailed === 0) {
      toast.success(`Import complete: ${centerOk} centres + ${memberOk} members created`);
    } else {
      toast.error(`Import done with errors: ${centerFailed + memberFailed} failed`);
    }
  };

  const pendingCount =
    centerStatuses.filter((s) => s.status === "pending").length +
    memberStatuses.filter((s) => s.status === "pending").length;

  const hasData = centers.length > 0 || members.length > 0;

  const downloadTemplate = () => {
    const XLSX = window.XLSX;
    if (!XLSX) {
      toast.error("Excel library not loaded. Please refresh and try again.");
      return;
    }
    const wb = generateTemplateWorkbook(XLSX);
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "SpandanOne_Onboarding_Template.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>Spandan - Import Onboarding</title>
      </Helmet>

      <Box className="main-wrappers">
        <Box className="page-wrapper" sx={{ pb: "24px", fontFamily: "Figtree" }}>
          <Box className="content">

            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ fontFamily: "Figtree" }}>
                  Bulk Onboarding
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Figtree", mt: 0.5 }}>
                  Upload the Excel template to create Centres and Members in bulk
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Icon icon="mdi:file-excel" />}
                onClick={downloadTemplate}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontFamily: "Figtree",
                  fontWeight: 600,
                  borderColor: "#5751E9",
                  color: "#5751E9",
                  "&:hover": { borderColor: "#4440d4", backgroundColor: "#f5f4ff" },
                }}
              >
                Download Template
              </Button>
            </Box>

            {xlsxError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                Failed to load the Excel parsing library. Please refresh the page and try again.
              </Alert>
            )}

            {fileErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 3, fontFamily: "Figtree" }}>
                <strong>File format error — please use the official template.</strong>
                <ul style={{ margin: "6px 0 0", paddingLeft: "20px" }}>
                  {fileErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </Alert>
            )}

            {/* Upload zone */}
            {!hasData && (
              <Paper
                elevation={0}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                sx={{
                  border: "2px dashed #D1D5DB",
                  borderRadius: "16px",
                  py: 8,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "border-color 0.2s",
                  "&:hover": { borderColor: "#5751E9" },
                }}
              >
                <Icon icon="mdi:cloud-upload-outline" width={48} color="#5751E9" />
                <Typography fontWeight={600} fontSize={16} mt={1} sx={{ fontFamily: "Figtree" }}>
                  Drop your Excel file here or click to upload
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ fontFamily: "Figtree" }}>
                  Supports .xlsx — use the template above for the correct format
                </Typography>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={handleFileChange}
                />
              </Paper>
            )}

            {/* File loaded — show re-upload + preview */}
            {hasData && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, backgroundColor: "var(--app-surface-2)", borderRadius: "8px" }}>
                    <Icon icon="mdi:file-excel" color="#22C55E" width={20} />
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "Figtree" }}>
                      {fileName}
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setCenters([]);
                      setMembers([]);
                      setCenterStatuses([]);
                      setMemberStatuses([]);
                      setFileErrors([]);
                      setFileName("");
                      setProgress(0);
                    }}
                    sx={{ textTransform: "none", fontFamily: "Figtree", color: "var(--app-text-muted)" }}
                  >
                    Upload different file
                  </Button>

                  {importing && (
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: "4px", height: "6px" }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Figtree" }}>
                        {progress}% complete
                      </Typography>
                    </Box>
                  )}

                  {!importing && pendingCount > 0 && (
                    <Button
                      variant="contained"
                      startIcon={<Icon icon="mdi:upload" />}
                      onClick={handleImport}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        backgroundColor: "#5751E9",
                        fontWeight: 600,
                        boxShadow: "none",
                        fontFamily: "Figtree",
                        ml: "auto",
                      }}
                    >
                      Import {pendingCount} Record{pendingCount !== 1 ? "s" : ""}
                    </Button>
                  )}
                </Box>

                {/* Tabs */}
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{ mb: 1, "& .MuiTab-root": { textTransform: "none", fontFamily: "Figtree", fontWeight: 600 } }}
                >
                  <Tab label={`Centres (${centers.length})`} />
                  <Tab label={`Members (${members.length})`} />
                </Tabs>

                {/* Validation summary */}
                {tab === 0 && centers.length > 0 && (() => {
                  const valid = centerStatuses.filter((s) => s.status === "pending").length;
                  const skipped = centerStatuses.filter((s) => s.status === "skipped").length;
                  return (
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                      <Chip label={`${valid} ready to import`} size="small" sx={{ borderRadius: "6px", backgroundColor: "#DAF4E1", color: "#348530", fontWeight: 700, fontFamily: "Figtree" }} />
                      {skipped > 0 && <Chip label={`${skipped} skipped — hover to see errors`} size="small" sx={{ borderRadius: "6px", backgroundColor: "#FFF3CD", color: "#E4841A", fontWeight: 700, fontFamily: "Figtree" }} />}
                    </Box>
                  );
                })()}
                {tab === 1 && members.length > 0 && (() => {
                  const valid = memberStatuses.filter((s) => s.status === "pending").length;
                  const skipped = memberStatuses.filter((s) => s.status === "skipped").length;
                  return (
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                      <Chip label={`${valid} ready to import`} size="small" sx={{ borderRadius: "6px", backgroundColor: "#DAF4E1", color: "#348530", fontWeight: 700, fontFamily: "Figtree" }} />
                      {skipped > 0 && <Chip label={`${skipped} skipped — hover to see errors`} size="small" sx={{ borderRadius: "6px", backgroundColor: "#FFF3CD", color: "#E4841A", fontWeight: 700, fontFamily: "Figtree" }} />}
                    </Box>
                  );
                })()}

                {/* Centers table */}
                {tab === 0 && (
                  <TableContainer className="app-table-card" component={Paper} elevation={0} sx={{ maxHeight: "65vh", overflowY: "auto" }}>
                    <Table stickyHeader sx={{ borderCollapse: "separate", borderSpacing: "0 6px" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headSx}>#</TableCell>
                          <TableCell sx={headSx}>NAME</TableCell>
                          <TableCell sx={headSx}>ROLE</TableCell>
                          <TableCell sx={headSx}>PHONE</TableCell>
                          <TableCell sx={headSx}>CITY</TableCell>
                          <TableCell sx={headSx}>STATE</TableCell>
                          <TableCell sx={headSx}>BILLING NAME</TableCell>
                          <TableCell sx={headSx}>TYPE</TableCell>
                          <TableCell sx={headSx}>STATUS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {centers.map((row, i) => (
                          <TableRow
                            key={i}
                            sx={{
                              "& td": { backgroundColor: "var(--app-surface)", borderBottom: "none", py: 1.2, fontSize: "14px", fontFamily: "Figtree" },
                              "& td:first-of-type": { borderTopLeftRadius: "8px", borderBottomLeftRadius: "8px" },
                              "& td:last-of-type": { borderTopRightRadius: "8px", borderBottomRightRadius: "8px" },
                            }}
                          >
                            <TableCell sx={{ color: "var(--app-text-muted)", fontWeight: 600 }}>{i + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{row.centreName || "—"}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.centerRole || "—"}
                                size="small"
                                sx={{ borderRadius: "4px", fontSize: "12px", fontFamily: "Figtree",
                                  backgroundColor: row.centerRole === "Hub Centre" ? "#EFF6FF" : "#F3E8FF",
                                  color: row.centerRole === "Hub Centre" ? "#1D4ED8" : "#7C3AED",
                                }}
                              />
                            </TableCell>
                            <TableCell>{row.phoneNumber || "—"}</TableCell>
                            <TableCell>{row.address_city || "—"}</TableCell>
                            <TableCell>{row.address_state || "—"}</TableCell>
                            <TableCell>{row.billing_name || "—"}</TableCell>
                            <TableCell>{row.type || "—"}</TableCell>
                            <TableCell>
                              <StatusChip
                                status={centerStatuses[i]?.status || "pending"}
                                error={centerStatuses[i]?.error}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {centers.length === 0 && (
                      <Typography textAlign="center" color="text.secondary" py={4} sx={{ fontFamily: "Figtree" }}>
                        No centre rows found in the Excel file
                      </Typography>
                    )}
                  </TableContainer>
                )}

                {/* Members table */}
                {tab === 1 && (
                  <TableContainer className="app-table-card" component={Paper} elevation={0} sx={{ maxHeight: "65vh", overflowY: "auto" }}>
                    <Table stickyHeader sx={{ borderCollapse: "separate", borderSpacing: "0 6px" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headSx}>#</TableCell>
                          <TableCell sx={headSx}>NAME</TableCell>
                          <TableCell sx={headSx}>EMAIL</TableCell>
                          <TableCell sx={headSx}>PHONE</TableCell>
                          <TableCell sx={headSx}>AGE</TableCell>
                          <TableCell sx={headSx}>ROLE</TableCell>
                          <TableCell sx={headSx}>DESIGNATION</TableCell>
                          <TableCell sx={headSx}>CENTRES</TableCell>
                          <TableCell sx={headSx}>STATUS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {members.map((row, i) => (
                          <TableRow
                            key={i}
                            sx={{
                              "& td": { backgroundColor: "var(--app-surface)", borderBottom: "none", py: 1.2, fontSize: "14px", fontFamily: "Figtree" },
                              "& td:first-of-type": { borderTopLeftRadius: "8px", borderBottomLeftRadius: "8px" },
                              "& td:last-of-type": { borderTopRightRadius: "8px", borderBottomRightRadius: "8px" },
                            }}
                          >
                            <TableCell sx={{ color: "var(--app-text-muted)", fontWeight: 600 }}>{i + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {[row.firstName, row.middleName, row.lastName].filter(Boolean).join(" ") || "—"}
                            </TableCell>
                            <TableCell>{row.email || "—"}</TableCell>
                            <TableCell>{row.phoneNumber || "—"}</TableCell>
                            <TableCell>{row.age || "—"}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.role || "—"}
                                size="small"
                                sx={{
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontFamily: "Figtree",
                                  backgroundColor: ROLE_COLORS[row.role]?.bg || "#F3F4F6",
                                  color: ROLE_COLORS[row.role]?.color || "var(--app-text)",
                                }}
                              />
                            </TableCell>
                            <TableCell>{row.designation || "—"}</TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Figtree" }}>
                                {row.centerIds || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <StatusChip
                                status={memberStatuses[i]?.status || "pending"}
                                error={memberStatuses[i]?.error}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {members.length === 0 && (
                      <Typography textAlign="center" color="text.secondary" py={4} sx={{ fontFamily: "Figtree" }}>
                        No member rows found in the Excel file
                      </Typography>
                    )}
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

const ROLE_COLORS = {
  "b2b-admin": { bg: "#EFF6FF", color: "#1D4ED8" },
  "b2b-member": { bg: "#F0FDF4", color: "#15803D" },
  "b2b-doctor": { bg: "#FDF4FF", color: "#7C3AED" },
  "b2b-viewer": { bg: "#FFF7ED", color: "#C2410C" },
};
